import React, { ReactElement } from "react";
import { Control, Controller, FormState, UseFormWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { ipRegex, specialCharactersValidation } from "constants/regexp";

import { IServerByTypeAPI } from "types/api/serversApiInterface";

type controlType = {
 server: number;
 upload_limit: number;
 db_host: string;
 port: number;
 db_name: string;
};

const ExternalDatabaseContent = ({
 control,
 formState,
 servers,
 watch,
 setValue,
 handleOpen,
 handleReset
}: {
 control: Control<controlType>;
 formState: FormState<controlType>;
 servers: Array<IServerByTypeAPI>;
 watch: UseFormWatch<controlType>;
 setValue: (type: keyof controlType, value: string | number) => void;
 handleOpen: () => void;
 handleReset: () => void;
}): ReactElement => {
 const intl = useIntl();

 const checkDisableConfirm = () => {
  return (
   watch("server") === 0 ||
   watch("upload_limit") === 0 ||
   watch("db_host") === "" ||
   watch("port") < 1024 ||
   watch("port") > 65535 ||
   watch("db_name") === "" ||
   !ipRegex.test(watch("db_host")) ||
   !specialCharactersValidation.test(watch("db_name"))
  );
 };

 return (
  <Stack spacing={2}>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="utility.externalDatabases.add.selectServer" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="utility.externalDatabases.add.selectServerMessage" />
      </Typography>
      <Controller
       name="server"
       control={control}
       render={({ field }) => (
        <Autocomplete
         fullWidth={true}
         autoHighlight
         sx={{ my: 2 }}
         onChange={(e, value) => {
          setValue("server", value?.id || 0);
         }}
         options={servers
          .filter((element) => !element.db_phpmyadmin_active)
          .map((server) => {
           return { label: server.server_name, id: server.id };
          })}
         value={servers
          .map((element) => {
           return { label: element.server_name, id: element.id };
          })
          .find((server) => server.id === watch("server"))}
         renderInput={(params) => (
          <TextField
           {...params}
           {...field}
           label={<FormattedMessage id="utility.externalDatabases.add.server" />}
           error={formState.isDirty && !!formState?.errors?.server}
           InputLabelProps={{ shrink: true }}
          />
         )}
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="utility.externalDatabases.add.uploadLimit" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="utility.externalDatabases.add.uploadLimitMessage" />
      </Typography>
      <Controller
       name="upload_limit"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         label={intl.formatMessage({ id: "utility.externalDatabases.add.uploadLimitField" })}
         error={formState.isDirty && !!formState?.errors?.upload_limit}
         InputLabelProps={{ shrink: true }}
         type="number"
         value={watch("upload_limit")}
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !!formState?.errors?.upload_limit &&
          intl.formatMessage({ id: "login.passwordMinLength" })
         }
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="utility.externalDatabases.add.databaseHost" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="utility.externalDatabases.add.databaseHostMessage" />
      </Typography>
      <Controller
       name="db_host"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("db_host")}
         label={intl.formatMessage({ id: "utility.externalDatabases.add.databaseHost" })}
         error={formState.isDirty && !ipRegex.test(watch("db_host"))}
         InputLabelProps={{ shrink: true }}
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !ipRegex.test(watch("db_host")) &&
          intl.formatMessage({ id: "utility.externalDatabases.add.databaseHostError" })
         }
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="utility.externalDatabases.add.port" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="utility.externalDatabases.add.portMessage" />
      </Typography>
      <Controller
       name="port"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("port")}
         label={intl.formatMessage({ id: "utility.externalDatabases.add.port" })}
         error={formState.isDirty && (watch("port") < 1024 || watch("port") > 65535)}
         InputLabelProps={{ shrink: true }}
         type="number"
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          (watch("port") < 1024 || watch("port") > 65535) &&
          intl.formatMessage({ id: "utility.externalDatabases.add.portError" })
         }
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="utility.externalDatabases.add.databaseName" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="utility.externalDatabases.add.databaseNameMessage" />
      </Typography>
      <Controller
       name="db_name"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("db_name")}
         label={intl.formatMessage({ id: "utility.externalDatabases.add.databaseName" })}
         error={formState.isDirty && !specialCharactersValidation.test(watch("db_name"))}
         InputLabelProps={{ shrink: true }}
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !specialCharactersValidation.test(watch("db_name")) &&
          intl.formatMessage({ id: "utility.externalDatabases.add.databaseNameSpecialCharacters" })
         }
        />
       )}
      />
     </Stack>
     <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
       <FormattedMessage id="app.wizard.reset" />
      </Button>
      <Button sx={{ mt: 1, mr: 1 }} disabled={checkDisableConfirm()} onClick={handleOpen}>
       <FormattedMessage id="app.wizard.confirm" />
      </Button>
     </Stack>
    </Stack>
   </Paper>
  </Stack>
 );
};

export default ExternalDatabaseContent;
